import { useEffect } from 'react'
import { ColorValue, Platform, StyleSheet } from 'react-native'
import Svg, { Defs, G, Mask, Path, Rect } from 'react-native-svg'
import Animated, {
  interpolate,
  interpolateColor,
  processColor,
  useAnimatedProps,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { colors } from '@ui/colors'

import { OVAL_PATH } from '../assets/ovalPath'
import {
  OUTLINE_INNER_COLOR,
  OUTLINE_INNER_WIDTH,
  OUTLINE_STATE_STYLES,
  OUTLINE_TRANSITION_DURATION,
  OutlineStateStyle,
} from '../styles'
import { OvalState, OvalLayout } from '../types'

const AnimatedPath = Animated.createAnimatedComponent(Path)

const isWeb = Platform.OS === 'web'

/**
 * On web, react-native-svg's `setNativeProps` (used by Reanimated for
 * animated props) only applies `stroke` when it receives a color brush
 * object `{ type: 0, payload: <processed color> }` — plain color strings
 * are silently ignored. On native, Reanimated processes color strings
 * itself, so we pass the string through untouched.
 */
const toStrokeValue = (color: string): ColorValue => {
  'worklet'
  if (!isWeb) {
    return color
  }
  const brush = { type: 0, payload: processColor(color) }
  return brush as unknown as ColorValue
}

/** Dash length assumed when a solid state has no dashed counterpart yet. */
const DEFAULT_DASH_LENGTH = 24
/** Gaps below this (in screen px) are visually solid. */
const SOLID_GAP_THRESHOLD = 0.1
/** Longer than the whole oval circumference — a single dash, i.e. solid. */
const SOLID_DASH_LENGTH = 1e4

/**
 * A solid stroke is represented as "one dash filling the whole pattern
 * period, zero gap". Keeping the period (dash + gap) constant during the
 * transition is what makes it look right: if the period changed, every
 * dash would shift cumulatively away from the path start, which reads as
 * a wave traveling clockwise around the oval. With a constant period each
 * dash simply grows/shrinks in place until neighbors merge or separate.
 */
const getDash = (style: OutlineStateStyle): [number, number] => {
  if (style.dash) {
    return [style.dash[0], style.dash[1]]
  }
  return [DEFAULT_DASH_LENGTH * 2, 0]
}

type Props = {
  layout: OvalLayout
  state: OvalState
}

/**
 * Two outlines stroked on the same oval path (not a scaled copy of it).
 * Strokes are centered on the path, so the drawn width is doubled and the
 * inner half is clipped away by a mask — the visible band always sits
 * outside the oval edge and its width stays constant in screen pixels
 * regardless of the oval scale.
 *
 * The outer outline transitions its color, width and dash pattern when
 * the state changes. Solid and dashed states morph into each other with a
 * constant pattern period (see `getDash`), and a dash-offset compensation
 * makes each dash grow/shrink from its center instead of its leading edge.
 */
export default function OvalOutlines({ layout, state }: Props) {
  const { width, height, transform, scale } = layout
  const target: OutlineStateStyle = OUTLINE_STATE_STYLES[state]
  const [initialDashLength, initialDashGap] = getDash(target)

  const progress = useSharedValue(1)
  const fromColor = useSharedValue(target.color)
  const toColor = useSharedValue(target.color)
  const fromWidth = useSharedValue(target.width)
  const toWidth = useSharedValue(target.width)
  const fromDashLength = useSharedValue(initialDashLength)
  const toDashLength = useSharedValue(initialDashLength)
  const fromDashGap = useSharedValue(initialDashGap)
  const toDashGap = useSharedValue(initialDashGap)
  /** Dash length of the dashed side of the transition — anchors dash centers. */
  const dashAnchor = useSharedValue(target.dash?.[0] ?? DEFAULT_DASH_LENGTH)

  useEffect(() => {
    // Capture the current (possibly mid-transition) values as the new
    // starting point, so rapid state changes don't jump.
    const value = progress.get()
    const capture = (from: number, to: number) =>
      interpolate(value, [0, 1], [from, to])
    fromColor.set(
      interpolateColor(value, [0, 1], [fromColor.get(), toColor.get()])
    )
    fromWidth.set(capture(fromWidth.get(), toWidth.get()))

    const capturedDashLength = capture(fromDashLength.get(), toDashLength.get())
    const capturedDashGap = capture(fromDashGap.get(), toDashGap.get())
    const isFromSolid = capturedDashGap < SOLID_GAP_THRESHOLD
    if (target.dash) {
      const [dashLength, dashGap] = target.dash
      // Coming from solid: any period looks identical, so adopt the
      // target's period to keep it constant during the transition.
      fromDashLength.set(
        isFromSolid ? dashLength + dashGap : capturedDashLength
      )
      fromDashGap.set(isFromSolid ? 0 : capturedDashGap)
      toDashLength.set(dashLength)
      toDashGap.set(dashGap)
      dashAnchor.set(dashLength)
    } else {
      fromDashLength.set(capturedDashLength)
      fromDashGap.set(capturedDashGap)
      // Going solid: the dash grows to fill the current period.
      toDashLength.set(
        isFromSolid ? capturedDashLength : capturedDashLength + capturedDashGap
      )
      toDashGap.set(0)
      dashAnchor.set(isFromSolid ? DEFAULT_DASH_LENGTH : capturedDashLength)
    }

    toColor.set(target.color)
    toWidth.set(target.width)
    progress.set(0)
    progress.set(withTiming(1, { duration: OUTLINE_TRANSITION_DURATION }))
  }, [
    target,
    progress,
    fromColor,
    toColor,
    fromWidth,
    toWidth,
    fromDashLength,
    toDashLength,
    fromDashGap,
    toDashGap,
    dashAnchor,
  ])

  const animatedProps = useAnimatedProps(() => {
    const between = (from: number, to: number) => {
      'worklet'
      return interpolate(progress.value, [0, 1], [from, to])
    }
    const outerWidth = between(fromWidth.value, toWidth.value)
    const dashLength = between(fromDashLength.value, toDashLength.value)
    const dashGap = between(fromDashGap.value, toDashGap.value)
    const isSolid = dashGap < SOLID_GAP_THRESHOLD
    // Positive offset pulls each dash backwards by half its growth,
    // so dashes expand/contract symmetrically around their centers.
    const dashOffset = (dashLength - dashAnchor.value) / 2
    return {
      stroke: toStrokeValue(
        interpolateColor(
          progress.value,
          [0, 1],
          [fromColor.value, toColor.value]
        )
      ),
      strokeWidth: (outerWidth * 2) / scale,
      strokeDasharray: isSolid
        ? [SOLID_DASH_LENGTH, 0]
        : [dashLength / scale, dashGap / scale],
      strokeDashoffset: isSolid ? 0 : dashOffset / scale,
    }
  })

  const innerStrokeWidth = (OUTLINE_INNER_WIDTH * 2) / scale

  return (
    <Svg
      width={width}
      height={height}
      style={StyleSheet.absoluteFill}
      pointerEvents="none"
    >
      <Defs>
        <Mask
          id="oval-outlines-outside"
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width={width}
          height={height}
        >
          <Rect x="0" y="0" width={width} height={height} fill={colors.white} />
          <Path d={OVAL_PATH} transform={transform} fill={colors.black} />
        </Mask>
      </Defs>
      <G mask="url(#oval-outlines-outside)">
        <G transform={transform}>
          <AnimatedPath
            d={OVAL_PATH}
            fill="none"
            animatedProps={animatedProps}
          />
          <Path
            d={OVAL_PATH}
            fill="none"
            stroke={OUTLINE_INNER_COLOR}
            strokeWidth={innerStrokeWidth}
          />
        </G>
      </G>
    </Svg>
  )
}
